/** The employee's front door.
 *
 * Four bands, in the order somebody in trouble actually needs them:
 *
 *   1. Who they are and what is waiting — greeting plus the one number that
 *      matters, "does anything need me right now".
 *   2. Search. The fastest resolution is the one where they never raise a
 *      ticket, so the box is the largest thing on the screen and it looks in
 *      knowledge *first*.
 *   3. Quick help — the six things people come here to do.
 *   4. Their own activity, then what actually moved recently.
 *
 * ## Every number here is counted, none is estimated
 *
 * The cards are arithmetic over `GET /api/portal/tickets/`, computed in
 * `activity.ts` and unit-tested there. Two cards the brief asked for —
 * Approvals and My assets — are deliberately absent: no portal API can answer
 * them (see docs/PORTAL-BACKEND-GAPS.md), and a fabricated count on a help
 * portal is worse than a missing one because somebody acts on it. They are in
 * the navigation with an honest state instead.
 */
import { useQuery } from '@tanstack/react-query'
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Clock,
  Inbox,
  LayoutGrid,
  type LucideIcon,
  MessageSquareReply,
  Search,
  Ticket as TicketIcon,
  TicketPlus,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { Badge } from '../../components/ui/badge'
import { Card } from '../../components/ui/card'
import { EmptyState } from '../../components/ui/empty-state'
import { Skeleton } from '../../components/ui/primitives'
import { api } from '../../lib/api'
import type { ArticleSummary, CatalogItem, TicketSummary } from '../../lib/types'
import { cn } from '../../lib/utils'
import { byRecentActivity, search, summarise } from '../activity'
import { useMe } from '../auth'
import { QUICK_ISSUES } from '../nav'
import { TicketRow } from '../components/TicketRow'

/** Morning/afternoon/evening from the reader's own clock. Small touch, and the
 *  one piece of warmth that costs nothing and needs no data. */
function greeting(now: Date): string {
  const hour = now.getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

export function Home() {
  const { data: me } = useMe()
  const [query, setQuery] = useState('')

  const tickets = useQuery({
    queryKey: ['tickets'],
    queryFn: () => api.get<TicketSummary[]>('/tickets/'),
  })
  const articles = useQuery({
    queryKey: ['kb'],
    queryFn: () => api.get<ArticleSummary[]>('/kb/'),
  })
  const catalog = useQuery({
    queryKey: ['catalog'],
    queryFn: () => api.get<CatalogItem[]>('/catalog/'),
  })

  const now = useMemo(() => new Date(), [])
  const rows = tickets.data ?? []
  const activity = useMemo(() => summarise(rows, now), [rows, now])
  const recent = useMemo(() => byRecentActivity(rows).slice(0, 4), [rows])

  const hits = useMemo(
    () =>
      search(query, {
        tickets: rows,
        articles: articles.data ?? [],
        services: catalog.data ?? [],
      }),
    [query, rows, articles.data, catalog.data],
  )

  const firstName = me?.name?.split(' ')[0] ?? ''

  return (
    <div className="space-y-6">
      {/* ------------------------------------------------------------ hero */}
      <section className="overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-surface to-surface p-6 sm:p-8">
        <p className="text-sm text-muted-foreground">
          {me?.organization ?? 'Logic Planet ITSM'}
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
          {greeting(now)}
          {firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="mt-1.5 max-w-xl text-sm text-muted-foreground">
          {activity.needsMyReply > 0
            ? `${activity.needsMyReply} of your tickets ${activity.needsMyReply === 1 ? 'is' : 'are'} waiting on your reply.`
            : 'Search for an answer, or tell us what you need — most things are fixed the same day.'}
        </p>

        {/* Search. Largest control on the page by design. */}
        <div className="relative mt-5 max-w-2xl">
          <label htmlFor="portal-search" className="sr-only">
            How can we help you?
          </label>
          <Search
            className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            id="portal-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="How can we help you?"
            autoComplete="off"
            aria-describedby="portal-search-hint"
            className="h-13 w-full rounded-xl border border-input bg-background py-3.5 pl-12 pr-4 text-base shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
          />
          <p id="portal-search-hint" className="sr-only">
            Searches your tickets, the knowledge base and the service catalog.
          </p>

          {query.trim().length >= 2 && (
            <SearchResults hits={hits} query={query} />
          )}
        </div>

        {/* Common problems, in the words people use for them. */}
        <div className="mt-3.5 flex flex-wrap gap-1.5">
          {QUICK_ISSUES.map((issue) => (
            <button
              key={issue.q}
              type="button"
              onClick={() => setQuery(issue.q)}
              className="rounded-full border border-border bg-background px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
            >
              {issue.label}
            </button>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------ quick help */}
      <section aria-labelledby="quick-help">
        <h2 id="quick-help" className="text-base font-semibold tracking-tight">
          What would you like to do?
        </h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <QuickAction
            to="/new"
            icon={TicketPlus}
            tone="primary"
            title="Raise a ticket"
            blurb="Something is broken or you need help."
          />
          <QuickAction
            to="/catalog"
            icon={LayoutGrid}
            tone="violet"
            title="Request a service"
            blurb="Equipment, access, software or a new starter."
          />
          <QuickAction
            to="/kb"
            icon={BookOpen}
            tone="success"
            title="Search knowledge"
            blurb="Step-by-step fixes for common problems."
          />
          <QuickAction
            to="/tickets"
            icon={TicketIcon}
            tone="info"
            title="My tickets"
            blurb="Track everything you have raised."
          />
        </div>
      </section>

      {/* -------------------------------------------------------- activity */}
      <section aria-labelledby="my-activity">
        <h2 id="my-activity" className="text-base font-semibold tracking-tight">
          My activity
        </h2>
        {tickets.isLoading ? (
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {Array.from({ length: 5 }, (_, index) => (
              <Skeleton key={index} className="h-[4.75rem]" />
            ))}
          </div>
        ) : (
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <StatCard
              to="/tickets?filter=open"
              icon={Inbox}
              tone="primary"
              label="Open tickets"
              value={activity.open}
            />
            <StatCard
              to="/tickets?filter=mine"
              icon={MessageSquareReply}
              tone="warning"
              label="Pending my reply"
              value={activity.needsMyReply}
              emphasis={activity.needsMyReply > 0}
            />
            <StatCard
              to="/tickets?filter=awaiting"
              icon={Clock}
              tone="info"
              label="Awaiting IT"
              value={activity.awaitingIt}
            />
            <StatCard
              to="/tickets?filter=resolved"
              icon={CheckCircle2}
              tone="success"
              label="Resolved this month"
              value={activity.resolvedThisMonth}
            />
            <StatCard
              to="/requests"
              icon={LayoutGrid}
              tone="violet"
              label="Service requests"
              value={activity.requests}
            />
          </div>
        )}
      </section>

      {/* ---------------------------------------------------------- recent */}
      <section aria-labelledby="recent-activity">
        <div className="flex items-center justify-between">
          <h2 id="recent-activity" className="text-base font-semibold tracking-tight">
            Recent activity
          </h2>
          {rows.length > 0 && (
            <Link
              to="/tickets"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              View all
              <ArrowRight className="size-3.5" aria-hidden />
            </Link>
          )}
        </div>

        {tickets.isLoading ? (
          <div className="mt-3 space-y-2">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} className="h-20" />
            ))}
          </div>
        ) : recent.length === 0 ? (
          <Card className="mt-3">
            <EmptyState
              bare
              icon={Inbox}
              title="Nothing raised yet"
              description="When you report a problem or request something, it will appear here so you can follow it."
              action={
                <Link
                  to="/new"
                  className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  <TicketPlus className="size-4" aria-hidden />
                  Raise a ticket
                </Link>
              }
            />
          </Card>
        ) : (
          <ul className="mt-3 space-y-2">
            {recent.map((ticket) => (
              <li key={ticket.ref}>
                <TicketRow ticket={ticket} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

/* --------------------------------------------------------------- search */

function SearchResults({
  hits,
  query,
}: {
  hits: ReturnType<typeof search>
  query: string
}) {
  const KIND_LABEL = { article: 'Help', service: 'Request', ticket: 'Your ticket' } as const

  return (
    <div
      role="region"
      aria-label="Search results"
      className="absolute inset-x-0 top-full z-20 mt-2 overflow-hidden rounded-xl border border-border bg-popover shadow-popover"
    >
      {hits.length === 0 ? (
        <div className="px-4 py-5 text-center">
          <p className="text-sm font-medium">No matches for “{query}”</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Tell us in your own words and someone will pick it up.
          </p>
          <Link
            to="/new"
            className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <TicketPlus className="size-3.5" aria-hidden />
            Raise a ticket
          </Link>
        </div>
      ) : (
        <>
          <ul className="max-h-80 overflow-y-auto py-1">
            {hits.map((hit) => (
              <li key={`${hit.kind}-${hit.id}`}>
                <Link
                  to={hit.to}
                  className="flex items-center gap-3 px-3 py-2 transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                >
                  <Badge variant="neutral" size="sm" className="shrink-0">
                    {KIND_LABEL[hit.kind]}
                  </Badge>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{hit.title}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {hit.subtitle}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          {/* Self-service first, but never a dead end. */}
          <div className="border-t border-border bg-surface px-3 py-2">
            <Link
              to="/new"
              className="flex items-center gap-2 text-xs font-medium text-primary hover:underline"
            >
              <TicketPlus className="size-3.5" aria-hidden />
              Still need help? Raise a ticket
            </Link>
          </div>
        </>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------- pieces */

const TONES = {
  primary: 'bg-primary/10 text-primary',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/15 text-warning',
  info: 'bg-info/10 text-info',
  violet: 'bg-chart-6/10 text-chart-6',
} as const

function QuickAction({
  to,
  icon: Icon,
  tone,
  title,
  blurb,
}: {
  to: string
  icon: LucideIcon
  tone: keyof typeof TONES
  title: string
  blurb: string
}) {
  return (
    <Link to={to} className="group block rounded-xl focus-visible:outline-none">
      <Card className="h-full rounded-xl p-4 transition-all group-hover:-translate-y-0.5 group-hover:border-primary/40 group-hover:shadow-popover group-focus-visible:border-primary group-focus-visible:ring-2 group-focus-visible:ring-ring/30">
        <span className={cn('grid size-10 place-items-center rounded-xl', TONES[tone])} aria-hidden>
          <Icon className="size-5" />
        </span>
        <h3 className="mt-3 text-sm font-semibold">{title}</h3>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{blurb}</p>
      </Card>
    </Link>
  )
}

function StatCard({
  to,
  icon: Icon,
  tone,
  label,
  value,
  emphasis = false,
}: {
  to: string
  icon: LucideIcon
  tone: keyof typeof TONES
  label: string
  value: number
  /** The one card that is a call to action rather than a statistic. */
  emphasis?: boolean
}) {
  return (
    <Link to={to} className="group block rounded-xl focus-visible:outline-none">
      <Card
        className={cn(
          'h-full rounded-xl p-3.5 transition-colors group-hover:border-primary/40 group-focus-visible:border-primary group-focus-visible:ring-2 group-focus-visible:ring-ring/30',
          emphasis && 'border-warning/40 bg-warning/5',
        )}
      >
        <div className="flex items-center gap-2.5">
          <span className={cn('grid size-8 shrink-0 place-items-center rounded-lg', TONES[tone])} aria-hidden>
            <Icon className="size-4" />
          </span>
          <p className="numeric text-2xl font-bold leading-none tracking-tight">{value}</p>
        </div>
        <p className="mt-2 text-xs font-medium leading-snug text-muted-foreground">{label}</p>
      </Card>
    </Link>
  )
}
